'use client';
import React, { useEffect, type ReactNode } from 'react';

type PointerPosition = Pick<PointerEvent, 'x' | 'y'>;

interface GlowCardProps {
  children: ReactNode;
  identifier: string;
}

const GlowCard: React.FC<GlowCardProps> = ({ children, identifier }) => {
  useEffect(() => {
    const CONTAINER = document.querySelector(
      `.glow-container-${identifier}`
    ) as HTMLElement | null;
    if (!CONTAINER) return;

    const CARDS = document.querySelectorAll(`.glow-card-${identifier}`);

    const CONFIG = {
      proximity: 40,
      spread: 80,
      blur: 12,
      gap: 32,
      vertical: false,
      opacity: 0,
    };

    // UPDATE only ever reads the pointer's viewport coordinates, so it takes
    // just those instead of a full PointerEvent. A real PointerEvent satisfies
    // this shape, so UPDATE still works as a `pointermove` listener, and the
    // priming call on mount below can pass a plain { x: 0, y: 0 } — which is
    // what the `as any` cast there used to be for.
    const UPDATE = (event: PointerPosition) => {
      for (const CARD of CARDS) {
        const cardElement = CARD as HTMLElement;
        const CARD_BOUNDS = cardElement.getBoundingClientRect();

        if (
          event.x > CARD_BOUNDS.left - CONFIG.proximity &&
          event.x < CARD_BOUNDS.left + CARD_BOUNDS.width + CONFIG.proximity &&
          event.y > CARD_BOUNDS.top - CONFIG.proximity &&
          event.y < CARD_BOUNDS.top + CARD_BOUNDS.height + CONFIG.proximity
        ) {
          cardElement.style.setProperty('--active', '1');
        } else {
          cardElement.style.setProperty('--active', CONFIG.opacity.toString());
        }

        const CARD_CENTER = [
          CARD_BOUNDS.left + CARD_BOUNDS.width * 0.5,
          CARD_BOUNDS.top + CARD_BOUNDS.height * 0.5,
        ];

        let ANGLE =
          (Math.atan2(event.y - CARD_CENTER[1], event.x - CARD_CENTER[0]) *
            180) /
          Math.PI;

        ANGLE = ANGLE < 0 ? ANGLE + 360 : ANGLE;

        cardElement.style.setProperty('--start', (ANGLE + 90).toString());
      }
    };

    document.body.addEventListener('pointermove', UPDATE);

    const RESTYLE = () => {
      CONTAINER.style.setProperty('--gap', `${CONFIG.gap}px`);
      CONTAINER.style.setProperty('--blur', `${CONFIG.blur}px`);
      CONTAINER.style.setProperty('--spread', `${CONFIG.spread}px`);
      CONTAINER.style.setProperty(
        '--direction',
        CONFIG.vertical ? 'column' : 'row'
      );
    };

    RESTYLE();
    // Prime the glow once on mount so cards are not left unstyled until the
    // pointer first moves.
    UPDATE({ x: 0, y: 0 });

    // Cleanup event listener
    return () => {
      document.body.removeEventListener('pointermove', UPDATE);
    };
  }, [identifier]);

  return (
    <div className={`glow-container-${identifier} glow-container`}>
      <article
        className={`glow-card glow-card-${identifier} h-fit cursor-pointer border border-[#2a2e5a] transition-all duration-300 relative bg-[#101123] text-gray-200 rounded-xl hover:border-transparent w-full`}
      >
        <div className="glows"></div>
        {children}
      </article>
    </div>
  );
};

export default GlowCard;
